import { buildMembersAuthHeaders } from './membersAuthHeaders';
import { ENV } from '../config/env';

/** What the app reads from core's GET /me. */
export interface Me {
  userId: number;
  /** The workspaces the caller may act inside, still active. */
  workspaceIds: number[];
  /** The modules the caller holds a usable license for, by catalog name; null
   *  when core could not read them. */
  products: string[] | null;
  /** Whether the caller administers the tenant (licenses, users). */
  tenantOwner: boolean;
}

/** Null when the session does not resolve to an active user. */
export async function fetchMe(
  getAccessToken: (forceRefresh?: boolean) => Promise<string>
): Promise<Me | null> {
  const token = await getAccessToken();
  const headers = buildMembersAuthHeaders(token, ENV.authzUserId, ENV.workspaceId);
  const res = await fetch(`${ENV.coreApi}/me`, { headers });
  if (!res.ok) return null;
  const body = (await res.json().catch(() => ({}))) as {
    user_id?: number;
    workspaces?: { id: number }[] | null;
    products?: string[] | null;
    tenant_owner?: boolean;
  };
  return {
    userId: body.user_id ?? 0,
    workspaceIds: Array.isArray(body.workspaces) ? body.workspaces.map((ws) => ws.id) : [],
    products: Array.isArray(body.products) ? body.products : null,
    tenantOwner: body.tenant_owner === true,
  };
}

let cached: Promise<Me | null> | null = null;

/** fetchMe, shared by the session check and the shell; a failure is not kept. */
export function loadMe(
  getAccessToken: (forceRefresh?: boolean) => Promise<string>
): Promise<Me | null> {
  if (cached) return cached;
  const p = fetchMe(getAccessToken).then(
    (me) => {
      if (me === null && cached === p) cached = null;
      return me;
    },
    (err: unknown) => {
      if (cached === p) cached = null;
      throw err;
    }
  );
  cached = p;
  return p;
}

/** A fresh /me, after something that changes it (a license); later loadMe calls share it. */
export function reloadMe(
  getAccessToken: (forceRefresh?: boolean) => Promise<string>
): Promise<Me | null> {
  cached = null;
  return loadMe(getAccessToken);
}

/** Test seam: forget the shared answer. */
export function clearMe(): void {
  cached = null;
}
